import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';

/** One run of the desktop headline: `accent` runs are sundown, the rest parchment. */
export interface AuthHeadlineSegment {
  text: string;
  accent?: boolean;
}

/**
 * Shared chrome for the pre-auth pages (login, register, reset-password): the
 * riverbed→starlight gradient background, centered flip* logo row, rounded
 * content panel, and bottom hero image. Each page projects its own heading/
 * form/links into the panel via the default content slot.
 */
@Component({
  selector: 'app-auth-layout',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './auth-layout.component.html',
  styleUrls: ['./auth-layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuthLayoutComponent {
  /** Set to false to drop the panel's background/shadow/radius, keeping only its width/padding. */
  @Input() panelBackground = true;

  /**
   * Optional tagline on the hero, below the logo, one column in from the left. Each is
   * shown only when set, so a screen opts in by passing its own copy and the rest show
   * nothing: `heroTitle` in parchment and `heroAccent` on the line below it in sundown.
   */
  @Input() heroTitle?: string;
  @Input() heroAccent?: string;

  /**
   * FLIP-564. The smaller line under the desktop split headline. Desktop split only: it is not part
   * of the mobile hero copy (`hasHeroCopy`), so phone widths never show it.
   */
  @Input() heroSubtitle?: string;

  /**
   * Which scene the mobile hero shows. `registration-complete` is the "You're in!" scene with
   * the mascot waving; anything else is the standard hero.
   */
  @Input() heroArt?: 'registration-complete';

  /**
   * Mobile only. Figma's frames without a tagline (claim discount 2234:2155, Create your
   * account 2234:1328) start the parchment panel at y 237, about 49px higher than the standard
   * hero's 286. A screen opts in with `[shortHero]="true"`; the default hero is unchanged.
   */
  @Input() shortHero = false;

  /**
   * FLIP-547. Opts the screen into the desktop split layout (Figma 1196 x 665): a hero column on the
   * left (logo, this headline, the scene) and the parchment panel on the right. Desktop and touch
   * tablets in landscape only - below that the screen is exactly as it was, so a screen that does not
   * pass this keeps the stacked layout everywhere.
   */
  @Input() desktopHeadline?: AuthHeadlineSegment[];

  get isSplit(): boolean {
    return !!this.desktopHeadline?.length;
  }

  get hasHeroCopy(): boolean {
    return !!(this.heroTitle || this.heroAccent);
  }
}
